import { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import getObjects, { ResponseData } from '~src/api/get/getObjects';
import {
  Props as CalculatorProps,
  RoomSetting,
  RoomNames,
  AreaQuery,
} from '~components/Calculator';

type Config = Pick<
  CalculatorProps,
  | 'roomsList'
  | 'employees'
  | 'meetingRooms'
  | 'offices'
  | 'kitchen'
  | 'orderTarget'
  | 'areaForOnePerson'
  | 'areaForOneMeetingRoom'
  | 'areaForOneOffice'
  | 'areaForOneKitchen'
  | 'query'
>;

type Value = Config & {
  totalOffices: number;
  totalKitchen: number;
  totalOpenSpace: number;
  totalMeetingRooms: number;
  totalArea: number;
  data: ResponseData;
  employees: RoomSetting;
  hasFirstRequestLoaded: boolean;
  decrementRoomValue: (name: RoomNames) => void;
  incrementRoomValue: (name: RoomNames) => void;
  setRoomValue: (name: RoomNames, value: number) => void;
  setEmployees: (value: number) => void;
  fetchObjects: () => void;
  getQuery: () => AreaQuery;
};

export type Props = {
  config: Config;
  children: (value: Value) => JSX.Element;
};

const getRoomValFromEmployeesCount = (
  defaultRoomSetting: RoomSetting['default'],
  roomValue: RoomSetting['value'],
  employeesCount: number,
): RoomSetting['value'] => {
  for (const employeesValThreshold in defaultRoomSetting) {
    const defaultVal = defaultRoomSetting[+employeesValThreshold];

    if (employeesCount >= +employeesValThreshold) {
      roomValue = defaultVal;
    }
  }
  return roomValue;
};

const CalculatorRenderProps = (props: Props) => {
  const [settings, setSettings] = useState(() => {
    const config = { ...props.config };

    config.roomsList.forEach((roomName) => {
      const roomSettings = config[roomName];

      roomSettings.value = getRoomValFromEmployeesCount(
        roomSettings.default,
        roomSettings.value,
        config.employees.value,
      );
    });

    return config;
  });

  const [objectsData, setObjectsData] = useState<ResponseData>({
    items: [],
    rents: '0',
    sales: '0',
  });
  const [hasFirstRequestLoaded, setHasFirstRequestLoaded] = useState(false);
  const setAreObjectsLoading = useState(false)[1];

  const {
    offices,
    kitchen,
    employees,
    meetingRooms,
    areaForOnePerson,
    areaForOneOffice,
    areaForOneKitchen,
    areaForOneMeetingRoom,
  } = settings;

  const totalOffices = offices.value * areaForOneOffice.value;
  const totalKitchen = kitchen.value * areaForOneKitchen.value;
  const totalOpenSpace = employees.value * areaForOnePerson.value;
  const totalMeetingRooms = meetingRooms.value * areaForOneMeetingRoom.value;

  const totalArea =
    totalOffices + totalKitchen + totalOpenSpace + totalMeetingRooms;

  const getQuery = (): AreaQuery => {
    const delta = Math.floor((totalArea / 100) * 10);

    return {
      ...props.config.query,
      areaTo: totalArea + delta,
      areaFrom: totalArea - delta,
    };
  };

  const setRoomValue = (name: RoomNames, value: number): void => {
    setSettings((settings) => {
      const settingsCopy = { ...settings };

      settingsCopy[name].value = value;

      return settingsCopy;
    });
  };

  const incrementRoomValue = (name: RoomNames): void => {
    setRoomValue(name, settings[name].value + 1);
  };

  const decrementRoomValue = (name: RoomNames): void => {
    const newVal = settings[name].value - 1;

    if (newVal >= 0) {
      setRoomValue(name, settings[name].value - 1);
    }
  };

  const setEmployees = (value: number): void => {
    setSettings((settings) => {
      const settingsCopy = { ...settings };

      settingsCopy.roomsList.forEach((roomName) => {
        const roomSettings = settingsCopy[roomName];
        roomSettings.value = getRoomValFromEmployeesCount(
          roomSettings.default,
          roomSettings.value,
          value,
        );
      });

      settingsCopy.employees.value = value;

      return settingsCopy;
    });
  };

  const fetchObjects = (): void => {
    setAreObjectsLoading(true);

    getObjects({
      query: getQuery(),
    }).then(({ data }) => {
      unstable_batchedUpdates(() => {
        if (!hasFirstRequestLoaded) {
          setHasFirstRequestLoaded(true);
        }
        setAreObjectsLoading(false);
        setObjectsData(data);
      });
    });
  };

  return props.children({
    ...settings,
    totalOffices,
    totalKitchen,
    totalOpenSpace,
    totalMeetingRooms,
    totalArea,
    employees,
    data: objectsData,
    decrementRoomValue,
    incrementRoomValue,
    setRoomValue,
    setEmployees,
    fetchObjects,
    getQuery,
    hasFirstRequestLoaded,
    offices,
    kitchen,
    meetingRooms,
    areaForOnePerson,
  });
};

export default CalculatorRenderProps;
